import React, { useCallback, useContext } from 'react';
import styled from 'styled-components';

import { media } from '@prisjakt/ui-base';

import { useIsAuthenticated } from 'src/hooks/use-is-authenticated';

import { ProductGridCard } from 'src/components/product-card-new';
import { AddToListButton } from 'src/components/action-buttons/add-to-list';
import HighlightedSearchResult from '../highlighted-search-result';
import { OffersGrid } from '../../offers-grid';
import { Ads } from '../../ads';
import { AdvizedCarousel } from '../../../../../components/advized';
import { useViewportContext } from 'src/components/viewport/context';
import { DealsContext } from '../deals-context';
import { isRaw } from '../../../utils/products';
import { LayoutMode } from 'src/components/layout-buttons/types';

import { ActiveCampaignByProductId } from 'src/hooks/active-campaign-by-product-id';
import { Product, Raw, Offer, ExpertCategoryContent } from 'src/types';
import { ProductComparisonProps } from '../types';
import { AdsLayoutConfig } from '../../ads/types';

import {
  ITEMS_DESKTOP,
  ITEMS_TABLET,
  ITEMS_MOBILE,
} from 'src/areas/product-collection/components/offers-grid/styles';

const IMAGES_PER_ROW_MOBILE = 2;

type ProductCardWrapperProps = {
  lazyLoadImage?: boolean;
  onClick?: (productId: string | number, name?: string, type?: string) => void;
  product: Product | Raw | Offer;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
  // experiment flag
  unfeaturedClickout?: boolean;
} & ProductComparisonProps;

const ProductCardWrapper = ({
  deselectProductById,
  isSelectingProducts,
  lazyLoadImage = true,
  onClick,
  product,
  productsWithActiveCampaign,
  selectProductById,
  selectedProducts,
  unfeaturedClickout,
}: ProductCardWrapperProps) => {
  const { getDealDiffPercentage } = useContext(DealsContext);
  const isAuthenticated = useIsAuthenticated();

  let id!: number;

  if (!isRaw(product)) {
    id = product.id;
  }

  const dealsDiffPercentage = productsWithActiveCampaign
    ? productsWithActiveCampaign[id]?.diffPercentage
    : getDealDiffPercentage(id);

  const onClickHandler = useCallback(
    () => onClick && onClick(id, product.name, product.__typename),
    [id]
  );

  const onSelect = useCallback(
    () => selectProductById && selectProductById(id, 'productCard'),
    [id, selectProductById]
  );

  const onDeselect = useCallback(
    () => deselectProductById && deselectProductById(id, 'productCard'),
    [id, deselectProductById]
  );

  const getPrice = () => {
    switch (product.__typename) {
      case 'Product':
        return product.priceSummary.regular;
      case 'Offer':
        return product.offerPrice.regular;
      case 'Raw':
        return product.price;
      default:
        return undefined;
    }
  };

  const disableUnfeaturedClickout =
    (product.__typename === 'Offer' || product.__typename === 'Raw') &&
    !unfeaturedClickout &&
    !product.store?.featured;

  return (
    <>
      {!isRaw(product) ? (
        <ProductGridCard
          brand={product.brand}
          diffPercentage={dealsDiffPercentage}
          highlightedName={
            product.highlight ? (
              <HighlightedSearchResult>
                {product.highlight}
              </HighlightedSearchResult>
            ) : (
              undefined
            )
          }
          imageSrc={product.media?.first || ''}
          shouldLazyLoadImage={lazyLoadImage}
          link={product.pathName}
          ActionArea={
            isAuthenticated &&
            !isSelectingProducts && (
              <>
                <AddToListButton
                  productId={product.id}
                  productName={product.name}
                />
              </>
            )
          }
          name={product.name}
          onClick={onClickHandler}
          onDeselect={onDeselect}
          onSelect={onSelect}
          price={product.priceSummary.regular}
          rank={product.popularity?.inCategory}
          rating={product.userReviewSummary?.rating}
          reviewCount={product.userReviewSummary?.count}
          properties={product.properties?.nodes}
          selectable={isSelectingProducts}
          selected={
            selectedProducts ? selectedProducts.some(p => p.id === id) : false
          }
        />
      ) : (
        <ProductGridCard
          type="offer"
          name={product.name}
          price={getPrice()}
          store={product.store}
          onClick={disableUnfeaturedClickout ? undefined : onClickHandler}
          link={disableUnfeaturedClickout ? undefined : product.externalUri}
          imageSrc={
            product.__typename === 'Offer' ? product.media?.first : undefined
          }
        />
      )}
    </>
  );
};

type Props = {
  feedbackPrompt?: React.ReactNode | null;
  feedbackPromptPosition?: number;
  onClick?: (productId: string | number, name?: string, type?: string) => void;
  productsWithActiveCampaign?: ActiveCampaignByProductId;
  products: (Product | Raw)[];
  expert?: ExpertCategoryContent | null;
  adslayoutConfig?: AdsLayoutConfig;
  isWide?: boolean;
  unfeaturedClickout?: boolean;
} & ProductComparisonProps;

export const GridLayout = ({
  feedbackPrompt,
  feedbackPromptPosition = 6,
  onClick,
  productsWithActiveCampaign,
  products,
  expert,
  adslayoutConfig,
  isWide,
  unfeaturedClickout,
  ...productComparisonProps
}: Props) => {
  const { isMobile } = useViewportContext();

  return (
    <OffersGrid
      offers={products.map((product, index) => {
        return (
          <ProductCardWrapper
            key={
              !isRaw(product)
                ? product.id
                : `${product.name}-${index.toString()}`
            }
            onClick={onClick}
            product={product}
            productsWithActiveCampaign={productsWithActiveCampaign}
            // Lazy loading is disabled for the first row of images (two first for mobile)
            // to enable images to load before React is loaded. The goal of this strategy
            // is to improve our Lighthouse performance score, and specifically the
            // "Largest Contentful Paint" metric.
            lazyLoadImage={index >= IMAGES_PER_ROW_MOBILE * 2}
            unfeaturedClickout={unfeaturedClickout}
            {...productComparisonProps}
          />
        );
      })}
      isWide={isWide}
    >
      <Ads
        offersLength={products.length}
        layout={LayoutMode.GRID}
        layoutConfig={adslayoutConfig}
      />

      {isMobile && expert && <AdvizedCarousel {...expert} grid />}

      {feedbackPrompt && (
        <FeedbackPrompt $position={feedbackPromptPosition} $isWide={isWide}>
          {feedbackPrompt}
        </FeedbackPrompt>
      )}
    </OffersGrid>
  );
};

type FeedbackPromptProps = {
  $position: number;
  $isWide?: boolean;
};

const FeedbackPrompt = styled.li<FeedbackPromptProps>`
  grid-row: ${p => p.$position};
  ${media.smallOnly`grid-column: 1 / span ${ITEMS_MOBILE}`}
  ${media.mediumOnly`grid-column: 1 / span ${ITEMS_TABLET}`}
  ${p => media.largeUp`grid-column: 1 / span ${p.$isWide ? 5 : ITEMS_DESKTOP}`}
`;
